import { App, AuthenticationError, AuthorizationError, Deployment, DeploymentLog, DeploymentStatus, NetworkError, Subscription, TaskStatus } from '@back4app2/sdk';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useState, useEffect, useReducer } from 'react';
import back4app2 from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { formatShortDateTime } from '../utils/dates';
import Timer from '../components/Timer';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import CancelBuildButton from '../components/CancelBuildButton';
import { ScreenHead, SurfaceCard } from '../components/ds';
import { CodeIcon, RollbackIcon } from '../components/ds/icons';
import Skeleton from '../components/Skeleton';
import { canRunAppActions } from '../components/MyAppActions';
import RollbackDialog from '../components/Rollback/RollbackDialog';
import { DeploymentActions, getDeploymentActions } from '../utils/apiV2';
import { hasFinishedBuilding } from '../utils/deploymentBuild';
import LogBox from '../components/LogBox';

interface MyAppDeploymentsState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  deployments?: Deployment[];
  selectedDeployment?: Deployment;
  isLoadingDeploymentLogs: boolean;
  deploymentLogsLoadingErrorMessage?: string;
  deploymentLogs?: DeploymentLog[];
}

const INITIAL_STATE: MyAppDeploymentsState = {
  isLoading: true,
  isLoadingDeploymentLogs: true,
};

enum MyAppDeploymentsActionType {
  RESET,
  FINISH_LOADING,
  SET_DEPLOYMENT,
  FINISH_LOADING_DEPLOYMENT_LOGS,
}

const finishLoading = (errorMessage?: string, deployments?: Deployment[]) =>
  ({
    type: MyAppDeploymentsActionType.FINISH_LOADING,
    payload: {
      errorMessage,
      deployments,
    },
  } as const);

const setSelectedDeployment = (
  errorMessage?: string,
  deployment?: Deployment
) =>
  ({
    type: MyAppDeploymentsActionType.SET_DEPLOYMENT,
    payload: {
      errorMessage,
      deployment,
    },
  } as const);

const finishLoadingDeploymentLogs = (
  errorMessage?: string,
  deploymentLogs?: DeploymentLog[]
) =>
  ({
    type: MyAppDeploymentsActionType.FINISH_LOADING_DEPLOYMENT_LOGS,
    payload: {
      errorMessage,
      deploymentLogs,
    },
  } as const);

const reset = () =>
  ({
    type: MyAppDeploymentsActionType.RESET,
  } as const);

type MyAppDeployemnstsAction =
  | ReturnType<typeof reset>
  | ReturnType<typeof finishLoading>
  | ReturnType<typeof setSelectedDeployment>
  | ReturnType<typeof finishLoadingDeploymentLogs>;

const reducer = (
  state: MyAppDeploymentsState = INITIAL_STATE,
  action: MyAppDeployemnstsAction
): MyAppDeploymentsState => {
  switch (action.type) {
    case MyAppDeploymentsActionType.RESET:
      return INITIAL_STATE;

    case MyAppDeploymentsActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        deployments: action.payload.deployments,
      };
    case MyAppDeploymentsActionType.SET_DEPLOYMENT:
      return {
        ...state,
        selectedDeployment: action.payload.deployment,
        deploymentLogsLoadingErrorMessage: undefined,
        isLoadingDeploymentLogs: true,
        deploymentLogs: undefined,
      };
    case MyAppDeploymentsActionType.FINISH_LOADING_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: false,
        deploymentLogsLoadingErrorMessage: action.payload.errorMessage,
        deploymentLogs: action.payload.deploymentLogs,
      };
  }
};

type PillTone = 'ok' | 'err' | 'busy' | 'idle';

const PILL_TONES: Record<PillTone, { pill: string; dot: string }> = {
  ok: { pill: 'border-state-ok/30 bg-state-ok/[0.12] text-state-ok-soft', dot: 'bg-state-ok-soft' },
  err: { pill: 'border-state-err/30 bg-state-err/[0.12] text-state-err', dot: 'bg-state-err' },
  busy: { pill: 'border-[#7EA6E8]/30 bg-[#7EA6E8]/[0.12] text-[#7EA6E8]', dot: 'bg-[#7EA6E8] animate-pulse' },
  idle: { pill: 'border-navy-400 bg-navy-800 text-navy-100', dot: 'bg-navy-300' },
};

/**
 * A deployment's state in one word. "Live" is the one serving the app — the
 * deployment the platform calls current — whatever its exact status.
 */
const deploymentPill = (deployment: Deployment, isCurrent: boolean): { label: string; tone: PillTone } => {
  const taskFailed = deployment.deployTask?.status === TaskStatus.FAILED;
  switch (deployment.status) {
    case DeploymentStatus.READY:
    case DeploymentStatus.RENEWING:
      return isCurrent ? { label: 'Live', tone: 'ok' } : { label: 'Ready', tone: 'ok' };
    case DeploymentStatus.SLEEPY:
    case DeploymentStatus.SLEEPING:
      return { label: 'Sleeping', tone: 'idle' };
    case DeploymentStatus.WAKING_UP:
      return { label: 'Waking up', tone: 'busy' };
    case DeploymentStatus.INITIALIZING:
    case DeploymentStatus.INITIALIZED:
    case DeploymentStatus.DEPLOYING:
      return taskFailed ? { label: 'Failed', tone: 'err' } : { label: 'Deploying', tone: 'busy' };
    case DeploymentStatus.QUEUED:
      return taskFailed ? { label: 'Failed', tone: 'err' } : { label: 'Queued', tone: 'busy' };
    case DeploymentStatus.CANCELING:
      return { label: 'Canceling', tone: 'idle' };
    case DeploymentStatus.CANCELED:
      return { label: 'Canceled', tone: 'idle' };
    case DeploymentStatus.DESTROYING:
      return { label: 'Destroying', tone: 'idle' };
    case DeploymentStatus.DESTROYED:
      return { label: 'Destroyed', tone: 'idle' };
    default:
      return { label: 'Failed', tone: 'err' };
  }
};

const StatusPill = ({ label, tone }: { label: string; tone: PillTone }) => (
  <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-ds-125 ${PILL_TONES[tone].pill}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${PILL_TONES[tone].dot}`} />
    {label}
  </span>
);

const KIND_LABEL: Record<DeploymentActions['source'], string> = {
  UPLOAD: 'Upload',
  GITHUB: 'Commit',
  IMAGE: 'Image',
};

// Without the row's actions (the API v2 was unreachable), what the list itself says.
const fallbackSource = (deployment: Deployment): DeploymentActions['source'] =>
  deployment.commitSHA ? 'GITHUB' : deployment.imageDigest ? 'IMAGE' : 'UPLOAD';

const fallbackHash = (deployment: Deployment) =>
  (deployment.commitSHA || deployment.imageDigest || '').replace(/^sha256:/, '').slice(0, 7) || undefined;

const ROLLBACK_HINTS: Partial<Record<DeploymentActions['rollback'], string>> = {
  AVAILABLE: 'Roll back to this deployment',
  IN_PROGRESS: 'Available when the deployment in progress finishes',
};

const IconButton = ({ label, onClick, isDisabled, children }: {
  label: string;
  onClick?: () => void;
  isDisabled?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={isDisabled}
    onClick={(event) => {
      event.stopPropagation();
      onClick?.();
    }}
    className="b4-focus w-9 h-9 flex items-center justify-center rounded-control text-navy-100 hover:bg-navy-600 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors duration-[120ms]"
  >
    {children}
  </button>
);

const MyAppDeployments = () => {
  const { app } = useOutletContext<{ app: App }>();
  const mainServiceEnvironment =
    app && app.mainService && app.mainService.mainServiceEnvironment;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  let {
    deploymentLogsLoadingErrorMessage,
    deployments,
    selectedDeployment,
    deploymentLogs,
    isLoadingDeploymentLogs
  } = state;
  const [open, setIsOpen] = useState(false);
  const [actions, setActions] = useState<Record<string, DeploymentActions>>({});
  const [rollbackTo, setRollbackTo] = useState<string | null>(null);
  const canRollback = !!app && canRunAppActions(app);
  const navigate = useNavigate();

  // Asked again whenever a deployment appears or changes status: that is
  // what moves "current" and "previous".
  const deploymentsKey = (deployments || []).map((d) => `${d.id}:${d.status}`).join(',');
  useEffect(() => {
    if (!app?.id || !deploymentsKey) return;
    let cancelled = false;
    getDeploymentActions(app.id)
      .then((result) => !cancelled && setActions(result))
      // Without it the rows just have no actions; the list itself still works.
      .catch((e) => console.error('could not load deployment actions', e));
    return () => {
      cancelled = true;
    };
  }, [app?.id, deploymentsKey]);

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_DEPLOYMENT_PAGE);
  }, []);

  useEffect(() => {
    let subscription: Subscription;

    if (mainServiceEnvironment?.id) {
      subscription = back4app2.subscribeToDeployments(
        mainServiceEnvironment.id,
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error("network error", error);
              dispatch(
                finishLoading(
                  "Network error when loading app deployments. Check your internet connection and try again."
                )
              );
            } else if (
              error instanceof AuthenticationError ||
              error instanceof AuthorizationError
            ) {
              window.location.replace(
                `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(
                  window.location.href
                )}`
              );
            } else {
              console.error("unexpected error loading app deployments", error);
              dispatch(
                finishLoading(
                  "Unexpected error when loading app deployments. Please try again."
                )
              );
            }
          } else {
            dispatch(finishLoading(undefined, snapshot));
          }
        }
      );
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
        dispatch(reset());
      }
    };
  }, [mainServiceEnvironment?.id]);

  useEffect(() => {
    let subscription: Subscription;

    if (selectedDeployment) {
      subscription = back4app2.subscribeToDeploymentLogs(
        selectedDeployment.id,
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error("network error", error);
              dispatch(
                finishLoadingDeploymentLogs(
                  "Network error when loading last deployment logs. Check your internet connection and try again."
                )
              );
            } else if (
              error instanceof AuthenticationError ||
              error instanceof AuthorizationError
            ) {
              window.location.replace(
                `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(
                  window.location.href
                )}`
              );
            } else {
              console.error(
                "unexpected error loading last deployment logs",
                error
              );
              dispatch(
                finishLoadingDeploymentLogs(
                  "Unexpected error when loading last deployment logs. Please try again."
                )
              );
            }
          } else {
            dispatch(finishLoadingDeploymentLogs(undefined, snapshot));
          }
        }
      );
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [selectedDeployment]);

  const handleExpand = (deployment: Deployment) => {
    if (!selectedDeployment) {
      setIsOpen(true);
      dispatch(setSelectedDeployment(undefined, deployment));
    } else if (selectedDeployment && selectedDeployment.id === deployment.id) {
      setIsOpen(false);
      dispatch(setSelectedDeployment(undefined, undefined));
    } else if (selectedDeployment && selectedDeployment.id !== deployment.id) {
      dispatch(setSelectedDeployment(undefined, deployment));
    }
  };

  const sorted = [...(deployments || [])].sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
  // Numbered oldest first; the API's number when it came, the list's otherwise.
  const numberOf = (deployment: Deployment, index: number) =>
    actions[deployment.id]?.number ?? sorted.length - index;

  return (
    <>
      {/* The count inside the title, so a phone keeps it on the same line. */}
      <ScreenHead
        title={
          <>
            Deployments
            {deployments && deployments.length ? (
              <span className="ml-3 align-middle font-courrier text-ds-135 font-normal tracking-normal text-navy-300">
                {deployments.length}
              </span>
            ) : null}
          </>
        }
      />

      {deployments ? (
        deployments.length > 0 ? (
          // flex-none: main is a flex column, and a card that clips its
          // overflow would otherwise shrink to the window instead of making
          // the page scroll — rows cut off, nothing to scroll to.
          <SurfaceCard className="flex-none overflow-hidden">
            {sorted.map((deployment, index) => {
              const isExpanded = open && selectedDeployment?.id === deployment.id;
              const rowActions = actions[deployment.id];
              const isCurrent = rowActions?.rollback === 'CURRENT';
              const pill = deploymentPill(deployment, isCurrent);
              const source = rowActions?.source || fallbackSource(deployment);
              const hash = rowActions?.codeHash || fallbackHash(deployment);
              const commitSubject =
                source === 'GITHUB' && deployment.commitMessage
                  ? deployment.commitMessage.split('\n')[0].trim()
                  : '';
              const showRollback =
                canRollback && !!rowActions && !!ROLLBACK_HINTS[rowActions.rollback];
              const finished = deployment.deployTask?.finishedAt instanceof Date;

              return (
                <div key={deployment.id} className="border-t border-navy-500 first:border-t-0">
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onClick={() => handleExpand(deployment)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleExpand(deployment);
                      }
                    }}
                    // On a phone: arrow, status and actions on the first line;
                    // what it built, its id, when and how long on the second.
                    className={`b4-focus flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors duration-[120ms] ${
                      isExpanded ? 'bg-navy-700' : 'hover:bg-navy-700/60'
                    } [@media(max-width:639.98px)]:flex-wrap [@media(max-width:639.98px)]:gap-x-3 [@media(max-width:639.98px)]:gap-y-2 [@media(max-width:639.98px)]:px-4`}
                  >
                    <span className="w-3 flex-none text-[11px] text-navy-300" aria-hidden>
                      {isExpanded ? '\u25BE' : '\u25B8'}
                    </span>
                    <span className="w-32 flex-none [@media(max-width:639.98px)]:w-auto">
                      <StatusPill {...pill} />
                    </span>
                    <span className="flex-1 min-w-0 [@media(max-width:639.98px)]:order-last [@media(max-width:639.98px)]:basis-full [@media(max-width:639.98px)]:pl-6">
                      <span className="flex items-baseline gap-2.5 min-w-0" title={deployment.commitMessage || undefined}>
                        <span className="text-ds-135 text-white whitespace-nowrap">
                          {KIND_LABEL[source]} #{numberOf(deployment, index)}
                        </span>
                        {hash ? <span className="font-courrier text-ds-125 text-navy-300 truncate">{hash}</span> : null}
                      </span>
                      <span className="flex items-center gap-1.5 mt-1 min-w-0 font-courrier text-ds-115 text-navy-300">
                        {/* A commit says what changed in its subject line, the one the
                            author wrote as the title: that first, the whole message on
                            hover. An upload's "message" is only the file count the
                            worker writes, so it has none here. */}
                        {commitSubject ? (
                          <>
                            <span className="truncate font-inter text-ds-125 text-navy-100" title={deployment.commitMessage}>
                              {commitSubject}
                            </span>
                            <span className="flex-none">·</span>
                          </>
                        ) : null}
                        <span className={commitSubject ? 'flex-none' : 'truncate'} title={deployment.id}>
                          {deployment.id.slice(0, 8)}
                        </span>
                        {/* When and how long: in their own column from sm up. */}
                        <span className="hidden [@media(max-width:639.98px)]:contents">
                          <span>·</span>
                          <span className="whitespace-nowrap">{formatShortDateTime(new Date(deployment.createdAt))}</span>
                          <span>·</span>
                          <Timer
                            realElapsedMS={deployment.deployTask?.elapsedTimeMS || 0}
                            hasFinished={finished}
                            className="whitespace-nowrap"
                          />
                        </span>
                      </span>
                    </span>

                    <span className="flex-none [@media(max-width:639.98px)]:ml-auto" onClick={(event) => event.stopPropagation()}>
                      <CancelBuildButton deployment={deployment} />
                    </span>

                    <span className="flex-none text-right [@media(max-width:639.98px)]:hidden">
                      <span className="block text-ds-135 text-navy-100 whitespace-nowrap">
                        {formatShortDateTime(new Date(deployment.createdAt))}
                      </span>
                      <Timer
                        realElapsedMS={deployment.deployTask?.elapsedTimeMS || 0}
                        hasFinished={finished}
                        className="mt-1 font-courrier text-ds-115 text-navy-300"
                      />
                    </span>

                    <span className="flex-none w-[4.75rem] flex items-center justify-end gap-1">
                      {rowActions?.view === 'CODE' ? (
                        <IconButton
                          label="View the code of this deployment"
                          onClick={() => navigate(`/apps/${app.id}/source?deployment=${deployment.id}`)}
                        >
                          <CodeIcon size={17} />
                        </IconButton>
                      ) : rowActions?.view === 'GITHUB' && rowActions.viewUrl ? (
                        <a
                          href={rowActions.viewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="View this commit on GitHub"
                          title="View this commit on GitHub"
                          onClick={(event) => event.stopPropagation()}
                          className="b4-focus w-9 h-9 flex items-center justify-center rounded-control text-navy-100 hover:bg-navy-600 hover:text-white transition-colors duration-[120ms]"
                        >
                          <CodeIcon size={17} />
                        </a>
                      ) : rowActions?.view === 'EXPIRED' ? (
                        <IconButton label="Only the current and previous deployments keep their code" isDisabled>
                          <CodeIcon size={17} />
                        </IconButton>
                      ) : null}
                      {showRollback ? (
                        <IconButton
                          label={ROLLBACK_HINTS[rowActions!.rollback] as string}
                          isDisabled={rowActions!.rollback !== 'AVAILABLE'}
                          onClick={() => setRollbackTo(deployment.id)}
                        >
                          <RollbackIcon size={17} />
                        </IconButton>
                      ) : null}
                    </span>
                  </div>

                  {/* The same well as the Overview's deployment log and the
                      Runtime Logs screen — one way to read a log line. */}
                  {isExpanded ? (
                    <div className="border-t border-navy-500">
                      <LogBox
                        logs={deploymentLogs}
                        isLoading={isLoadingDeploymentLogs}
                        errorMessage={deploymentLogsLoadingErrorMessage}
                        hasFinished={hasFinishedBuilding(deployment)}
                        canMaximize
                        fit
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </SurfaceCard>
        ) : (
          <SurfaceCard tone="surface" className="px-4 py-10 text-center text-ds-135 text-navy-300">
            Nothing deployed yet.
          </SurfaceCard>
        )
      ) : !mainServiceEnvironment?.id ? (
        <SurfaceCard tone="surface" className="px-4 py-10 text-center text-ds-135 text-navy-300">
          Nothing deployed yet.
        </SurfaceCard>
      ) : (
        /* Was a bare line of text — a skeleton at least says how much is coming. */
        <SurfaceCard className="flex-none overflow-hidden">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center gap-4 px-5 py-4 border-t border-navy-500 first:border-t-0">
              <Skeleton className="h-6 w-24 rounded-full" />
              <span className="flex-1 flex flex-col gap-2">
                <Skeleton className="h-4 w-40 rounded-[3px]" />
                <Skeleton className="h-3 w-20 rounded-[3px]" />
              </span>
            </div>
          ))}
        </SurfaceCard>
      )}

      {rollbackTo ? (
        <RollbackDialog
          appId={app.id}
          deploymentId={rollbackTo}
          open
          onClose={() => setRollbackTo(null)}
        />
      ) : null}
    </>
  );
};

export default MyAppDeployments;
